import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Trophy, Flame, RefreshCw, Users, ChevronDown, ChevronUp } from "lucide-react";

const API = '/api';

export default function MobileScores() {
  const { eventId } = useParams();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(eventId || "");
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [expandedShooter, setExpandedShooter] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      fetchLeaderboard();
      // Auto-refresh every 15 seconds
      const interval = setInterval(fetchLeaderboard, 15000);
      return () => clearInterval(interval);
    }
  }, [selectedEvent]);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/events`);
      setEvents(res.data);
      if (!eventId && res.data.length > 0) {
        // Auto-select first active event
        const activeEvent = res.data.find(e => e.status === "active") || res.data[0];
        setSelectedEvent(activeEvent.id);
      }
    } catch (error) {
      console.error("Failed to fetch events");
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const res = await axios.get(`${API}/leaderboard/${selectedEvent}`);
      setLeaderboardData(res.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Failed to fetch leaderboard");
    }
  };

  const getRankStyle = (rank) => {
    if (rank === 1) return "bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900";
    if (rank === 2) return "bg-gradient-to-r from-slate-300 to-slate-400 text-slate-800";
    if (rank === 3) return "bg-gradient-to-r from-amber-500 to-amber-600 text-amber-900";
    return "bg-slate-100 text-slate-600";
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return "🥇";
    if (rank === 2) return "🥈";
    if (rank === 3) return "🥉";
    return `#${rank}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F7F4] flex items-center justify-center">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F7F4]" data-testid="mobile-scores-page">
      {/* Header */}
      <header className="bg-[#1B3B6F] text-white sticky top-0 z-10 shadow-lg">
        <div className="px-4 py-3">
          <h1 className="font-serif text-xl font-bold">NMLRA Scores</h1>
          <div className="flex items-center gap-2 mt-1 text-white/70 text-sm">
            <RefreshCw size={12} className="animate-spin" />
            <span>Auto-updating</span>
            {lastUpdated && (
              <span>• {lastUpdated.toLocaleTimeString()}</span>
            )}
          </div>
        </div>
        
        {/* Event Selector */}
        <div className="px-4 pb-3">
          <select
            value={selectedEvent}
            onChange={(e) => setSelectedEvent(e.target.value)}
            className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
            data-testid="mobile-event-select"
          >
            {events.map((event) => (
              <option key={event.id} value={event.id} className="text-slate-800">
                {event.name} {event.status === "completed" ? "(Completed)" : ""}
              </option>
            ))}
          </select>
        </div>
      </header>

      {/* Event Info Banner */}
      {leaderboardData?.event && (
        <div className="bg-white border-b px-4 py-3">
          <div className="flex items-center gap-3">
            <Trophy className="text-amber-500" size={24} />
            <div className="flex-1">
              <h2 className="font-semibold text-slate-800">
                {leaderboardData.event.name}
              </h2>
              <p className="text-xs text-slate-500">
                {leaderboardData.shots_per_round} shots per round
                {leaderboardData.is_team_event && " • Team Event"}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard */}
      <div className="px-4 py-4">
        {leaderboardData?.leaderboard?.length > 0 ? (
          <div className="space-y-2">
            {leaderboardData.leaderboard.map((entry, idx) => (
              <div
                key={entry.shooter_id || entry.team_name}
                className="bg-white rounded-xl shadow-sm overflow-hidden"
                data-testid={`mobile-rank-${entry.rank}`}
              >
                {/* Main Row */}
                <div 
                  className="flex items-center p-3 cursor-pointer"
                  onClick={() => setExpandedShooter(
                    expandedShooter === (entry.shooter_id || entry.team_name) 
                      ? null 
                      : (entry.shooter_id || entry.team_name)
                  )}
                >
                  {/* Rank Badge */}
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${getRankStyle(entry.rank)}`}>
                    {entry.rank <= 3 ? getRankBadge(entry.rank) : entry.rank}
                  </div>
                  
                  {/* Name & Team */}
                  <div className="flex-1 ml-3">
                    <p className="font-semibold text-slate-800">
                      {leaderboardData.is_team_event ? entry.team_name : entry.shooter_name}
                    </p>
                    {leaderboardData.is_team_event && entry.member_count && (
                      <p className="text-xs text-slate-500 flex items-center gap-1">
                        <Users size={12} /> {entry.member_count} members
                      </p>
                    )}
                  </div>
                  
                  {/* Score */}
                  <div className="text-right">
                    <p className="font-mono text-2xl font-bold text-green-700">
                      {entry.best_score}
                    </p>
                    <p className="text-xs text-slate-400">
                      /{leaderboardData.shots_per_round}
                    </p>
                  </div>
                  
                  {/* Expand Icon */}
                  <div className="ml-2 text-slate-400">
                    {expandedShooter === (entry.shooter_id || entry.team_name) 
                      ? <ChevronUp size={20} /> 
                      : <ChevronDown size={20} />
                    }
                  </div>
                </div>
                
                {/* Expanded Details */}
                {expandedShooter === (entry.shooter_id || entry.team_name) && (
                  <div className="bg-slate-50 px-4 py-3 border-t grid grid-cols-3 gap-4 text-center">
                    {leaderboardData.track_streak && (
                      <div>
                        <p className="text-xs text-slate-500 uppercase">Streak</p>
                        <p className="font-mono text-lg font-bold text-orange-600 flex items-center justify-center gap-1">
                          <Flame size={16} /> {entry.best_streak}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Rounds</p>
                      <p className="font-mono text-lg font-bold text-slate-700">
                        {entry.total_rounds}
                      </p>
                    </div>
                    {leaderboardData.is_team_event && (
                      <div>
                        <p className="text-xs text-slate-500 uppercase">Total Hits</p>
                        <p className="font-mono text-lg font-bold text-[#1B3B6F]">
                          {entry.total_hits}
                        </p>
                      </div>
                    )}
                    {!leaderboardData.is_team_event && entry.all_scores && (
                      <div>
                        <p className="text-xs text-slate-500 uppercase">Avg</p>
                        <p className="font-mono text-lg font-bold text-[#1B3B6F]">
                          {(entry.all_scores.reduce((a,b) => a+b, 0) / entry.all_scores.length).toFixed(1)}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Trophy className="mx-auto text-slate-300 mb-3" size={48} />
            <p className="text-slate-500">No scores yet</p>
            <p className="text-sm text-slate-400 mt-1">Check back soon!</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="text-center py-6 text-xs text-slate-400">
        National Muzzle Loading Rifle Association
      </footer>
    </div>
  );
}
